import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import type { Testimonio } from "@/types/content";

type Draft = { id?: number; nombre: string; cargo: string; texto: string; inicial: string; color: string };

const COLORS = ["bg-primary-500", "bg-secondary-500", "bg-accent-500"];

export default function AdminTestimonios() {
  const [items, setItems] = useState<Testimonio[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/bootstrap`);
    setItems((await res.json()).testimonios);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(editing.id ? `${API_BASE}/testimonios/${editing.id}` : `${API_BASE}/testimonios`, {
        method: editing.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || "No se pudo guardar");
        return;
      }
      setEditing(null);
      await load();
    } catch {
      setError("Error de conexión.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar este testimonio?")) return;
    await fetch(`${API_BASE}/testimonios/${id}`, { method: "DELETE" });
    await load();
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
  const labelCls = "block text-xs font-medium text-foreground-600 mb-1";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">Testimonios</h1>
        <button
          type="button"
          onClick={() => setEditing({ nombre: "", cargo: "", texto: "", inicial: "", color: COLORS[0] })}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          + Agregar testimonio
        </button>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  {t.nombre} <span className="text-xs text-foreground-400">— {t.cargo}</span>
                </p>
                <p className="mt-1 text-xs text-foreground-500">{t.texto}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <button type="button" onClick={() => setEditing(t)} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                  Editar
                </button>
                <button type="button" onClick={() => handleDelete(t.id)} className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div className="w-full max-w-lg rounded-lg bg-background-50 p-6 mt-10">
            <h2 className="font-heading font-semibold text-lg text-foreground-900">
              {editing.id ? "Editar testimonio" : "Nuevo testimonio"}
            </h2>
            {error && <div className="mt-3 rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>}
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Nombre</label>
                <input className={inputCls} value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Cargo / empresa</label>
                <input className={inputCls} value={editing.cargo} onChange={(e) => setEditing({ ...editing, cargo: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Testimonio</label>
                <textarea className={inputCls} rows={4} value={editing.texto} onChange={(e) => setEditing({ ...editing, texto: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Iniciales (ej. CM)</label>
                  <input
                    className={inputCls}
                    maxLength={2}
                    value={editing.inicial}
                    onChange={(e) => setEditing({ ...editing, inicial: e.target.value.toUpperCase() })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Color del avatar</label>
                  <select className={inputCls} value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })}>
                    {COLORS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-foreground-600 cursor-pointer">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
